/**
 * Ni2ScopeSelector — one compact dropdown reused above every Ni²
 * composer. Four options: My Memory · Department · Literature ·
 * Synthesize All. Reads/writes the shared Ni2ScopeContext so the
 * choice persists across every Ni² entry point within the session.
 */
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, User, Building2, BookOpen, Layers } from "lucide-react";
import { useNi2Scope } from "@/context/Ni2ScopeContext";

const ICONS = {
  personal:   User,
  department: Building2,
  literature: BookOpen,
  synthesize: Layers,
};

const HINTS = {
  personal:   "Only records you personally own",
  department: "Departmental memory (excludes your personal-scope records)",
  literature: "Representative literature records only",
  synthesize: "Personal + Department + Literature, kept distinct",
};

export default function Ni2ScopeSelector({ testIdPrefix = "ni2-scope" }) {
  const { scope, setScope, options, label } = useNi2Scope();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const ActiveIcon = ICONS[scope] || User;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-flex" data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-trigger`}
        data-scope={scope}
        className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium tracking-tight transition-colors"
        style={{
          background:
            "linear-gradient(180deg, rgba(160,200,255,0.16), rgba(120,165,240,0.06))",
          borderColor: "rgba(160,200,255,0.30)",
          color: "hsl(var(--nn-ink))",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
        }}
        title="Ni² retrieval scope"
      >
        <span
          className="grid h-4 w-4 place-items-center rounded-full"
          style={{
            background: "rgba(160,200,255,0.22)",
            color: "hsl(var(--nn-blue))",
          }}
        >
          <ActiveIcon className="h-2.5 w-2.5" strokeWidth={2} />
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">Scope</span>
        <span data-testid={`${testIdPrefix}-current-label`}>{label}</span>
        <ChevronDown className="h-3 w-3 opacity-70" strokeWidth={2} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid={`${testIdPrefix}-menu`}
          className="nn-popover nn-soft-in absolute left-0 bottom-full z-40 mb-2 w-[264px] p-1.5"
          style={{ borderRadius: 14 }}
        >
          <div className="px-2 pb-1 pt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
            Retrieval scope
          </div>
          {options.map((opt) => {
            const Icon = ICONS[opt.value] || User;
            const active = opt.value === scope;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                data-testid={`${testIdPrefix}-option-${opt.value}`}
                onClick={() => { setScope(opt.value); setOpen(false); }}
                className="nn-popover-item flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left"
                style={active ? {
                  background: "rgba(160,200,255,0.14)",
                  borderColor: "rgba(160,200,255,0.30)",
                } : undefined}
              >
                <span
                  className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border border-white/10 bg-white/[0.04]"
                  style={{ color: active ? "hsl(var(--nn-blue))" : "rgba(255,255,255,0.72)" }}
                >
                  <Icon className="h-3 w-3" strokeWidth={1.8} />
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--nn-ink))]">
                    {opt.label}
                    {active && <Check className="h-3 w-3 text-[hsl(var(--nn-ok))]" strokeWidth={2.2} />}
                  </span>
                  <span className="mt-0.5 block text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
                    {HINTS[opt.value]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
